import React from "react";

import "froala-editor/js/froala_editor.pkgd.min.js";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/js/plugins/code_view.min.js";
import "froala-editor/js/plugins/font_family.min.js";
import "froala-editor/js/plugins/font_size.min.js";
import "froala-editor/js/plugins/image.min.js";
import "froala-editor/js/plugins/video.min.js";
import "froala-editor/js/third_party/embedly.min.js";

import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/plugins/image.min.css";
import "froala-editor/css/plugins/video.min.css";
import "froala-editor/css/third_party/embedly.min.css";

import { FroalaUploadRequest } from "@piccadilly-cloud/platform-services";
import Froala from "react-froala-wysiwyg";

export default function FroalaEditor(props: {
  editorKey: string;
  defaultHtmlValue: string;
  onChange: (nextHtmlValue: string) => void;
  assetManagerImageOpts: () => FroalaUploadRequest;
  defaultText?: string;
  canvasEditor?: boolean;
  heightMax?: number;
}) {
  const [isFroalaInitialized, setIsFroalaInitialized] = React.useState(false);
  const [model, setModel] = React.useState<string>("");
  const [currentEditorKey, setCurrentEditorKey] = React.useState("");

  const defaultValueCB = React.useCallback(() => {
    if (!isFroalaInitialized && props.editorKey !== "") {
      setIsFroalaInitialized(true);
      setCurrentEditorKey(props.editorKey);
      setModel(props.defaultHtmlValue);
    }
    if (props.editorKey !== currentEditorKey) {
      setCurrentEditorKey(props.editorKey);
      setModel(props.defaultHtmlValue);
    }
  }, [
    currentEditorKey,
    isFroalaInitialized,
    props.defaultHtmlValue,
    props.editorKey,
  ]);
  React.useEffect(defaultValueCB, [defaultValueCB, props.editorKey]);

  const handleModelChange = (model: any, others: any) => {
    if (props.editorKey === currentEditorKey) {
      props.onChange(model);
      setModel(model);
    }
  };

  return (
    <Froala
      model={model}
      onModelChange={handleModelChange}
      config={{
        key: process.env.REACT_APP_F_LICENSE,
        attribution: false,
        placeholder: "Start typing...",
        heightMin: props.canvasEditor ? "20rem" : "12rem",
        heightMax: props.heightMax || "auto",
        zIndex: 9990,
        placeholderText: props.defaultText
          ? props.defaultText
          : "Start typing...",
        videoUploadParam: "file",
        videoUploadURL: `${process.env.REACT_APP_EDGE_URL}/upload/froala`,
        videoUploadParams: {
          request: JSON.stringify(props.assetManagerImageOpts()),
        },
        videoUploadMethod: "POST",
        videoMaxSize: 50 * 1024 * 1024,
        videoAllowedTypes: [
          "webm",
          "jpg",
          "ogg",
          "mov",
          "MOV",
          "mp4",
          "quicktime",
        ],
        videoAllowedProviders: ["youtube", "vimeo"],

        imageUploadURL: `${process.env.REACT_APP_EDGE_URL}/upload/froala`,
        imageUploadParam: "file",
        imageUploadMethod: "POST",
        imageUploadParams: {
          request: JSON.stringify(props.assetManagerImageOpts()),
        },
        imageUploadResponse: (resp: string) => {
          // Froala expects a JSON object with { link: "https://..." }.
          // Normalize responses to avoid the vague "Parsing response failed" error.
          try {
            const parsed = JSON.parse(resp);

            if (typeof parsed?.link === "string") return resp;

            const link =
              parsed?.data?.link ||
              parsed?.data?.url ||
              parsed?.data?.location ||
              parsed?.data?.fileUrl ||
              parsed?.data?.href ||
              parsed?.data?.file?.url ||
              parsed?.data?.file?.link ||
              parsed?.data?.files?.[0]?.url ||
              parsed?.data?.files?.[0]?.link;

            if (typeof link === "string" && link.length > 0) {
              return JSON.stringify({ link });
            }

            if (
              parsed?.status === "error" &&
              typeof parsed?.message === "string"
            ) {
              console.error("image upload failed", parsed);
              return JSON.stringify({ link: "", error: parsed.message });
            }
          } catch (err) {
            console.error("imageUploadResponse error", err, resp);
          }

          return JSON.stringify({ link: "", error: "Upload failed" });
        },
        htmlExecuteScripts: false,
        events: {
          "image.beforeUpload": (img: any) => {
            console.log("what do we have here", img);
          },
          "image.uploaded": (resp: any) => {
            console.log("the resp", resp);
          },
          "image.error": (err: any, resp: any) => {
            console.log("image.err", err, resp.message);
          },
        },
        fontSize: [
          "8",
          "10",
          "12",
          "14",
          "16",
          "18",
          "24",
          "30",
          "36",
          "48",
          "60",
          "72",
        ],
        fontSizeDefaultSelection: "14",
        fontFamily: {
          "Open Sans,Helvetica,sans-serif": "Open Sans",
        },
        fontFamilySelection: true,
        fontFamilyDefaultSelection: "Open Sans",
        toolbarButtons: {
          moreText: {
            buttons: [
              "bold",
              "italic",
              "underline",
              "fontSize",
              "strikeThrough",
              "subscript",
              "superscript",
              "fontFamily",
              "textColor",
              "backgroundColor",
              "inlineClass",
              "inlineStyle",
              "clearFormatting",
            ],
          },
          moreParagraph: {
            buttons: [
              "alignLeft",
              "alignCenter",
              "formatOLSimple",
              "alignRight",
              "alignJustify",
              "formatOL",
              "formatUL",
              "paragraphFormat",
              "paragraphStyle",
              "lineHeight",
              "outdent",
              "indent",
              "quote",
            ],
          },
          moreRich: {
            buttons: [
              "insertLink",
              "insertImage",
              "insertVideo",
              "insertTable",
              "emoticons",
              "fontAwesome",
              "specialCharacters",
              "embedly",
              "insertHR",
            ],
          },
          moreMisc: {
            buttons: [
              "undo",
              "redo",
              "fullscreen",
              "print",
              "spellChecker",
              "selectAll",
              "html",
              "help",
            ],
            align: "right",
            buttonsVisible: 2,
          },
        },
        pluginsEnabled: [
          "table",
          "spell",
          "quote",
          "save",
          "paragraphFormat",
          "paragraphStyle",
          "help",
          "draggable",
          "fontFamily",
          "fontSize",
          "align",
          "link",
          "lists",
          "file",
          "image",
          "emoticons",
          "url",
          "video",
          "embedly",
          "colors",
          "entities",
          "inlineClass",
          "inlineStyle",
          "spellChecker",
          "imageTUI",
          "fullscreen",
          "codeView",
        ],
      }}
    />
  );
}
